import {
  analyticsRoutes,
  configuration,
  investigatorRoutes,
  manageDataRoute,
  userManagementRoutes,
} from "../routes/routeConstants";
import {
  hasFullAccess,
  hasAnyPermission,
  hasPermission,
} from "./permissions";
import {
  BATCHES_PERMISSIONS,
  CONFIGURATION_PERMISSIONS,
  DASHBOARD_PERMISSIONS,
  GENEALOGY_PERMISSIONS,
  LIMITS_PERMISSIONS,
  MANAGE_DATA_MODULE,
  MANAGE_DATA_PERMISSIONS,
  PERMISSION_MODULES,
  PROCESS_PERMISSIONS,
  REPORT_PERMISSIONS,
  USER_ACCESS_MANAGEMENT_PERMISSIONS,
  VISUALIZATION_PERMISSIONS,
} from "../constants/permissions";

export const getSrmLandingRoute = () => {
  if (
    hasPermission(
      PERMISSION_MODULES.VISUALIZATION,
      VISUALIZATION_PERMISSIONS.VIEW_CHART_LIST,
    )
  ) {
    return `/${analyticsRoutes.visualizationInterface}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.VISUALIZATION,
      VISUALIZATION_PERMISSIONS.CREATE_NEW_CHART,
    )
  ) {
    return `/${analyticsRoutes.visualizationInterface}/charts/create`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.PROCESS,
      PROCESS_PERMISSIONS.VIEW_PARAMETER_LIST,
    )
  ) {
    return `/${analyticsRoutes.process}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.LIMITS,
      LIMITS_PERMISSIONS.VIEW_LIMITS_LIST,
    )
  ) {
    return `/${analyticsRoutes.limit}`;
  }
  if (
    hasAnyPermission(PERMISSION_MODULES.BATCHES, [
      BATCHES_PERMISSIONS.VIEW_BATCH_LIST,
      BATCHES_PERMISSIONS.PARTIAL_DATA_TAB,
    ])
  ) {
    return `/${analyticsRoutes.batches}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.REPORT,
      REPORT_PERMISSIONS.VIEW_REPORT_LIST,
    )
  ) {
    return `/${analyticsRoutes.analytics}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.REPORT,
      REPORT_PERMISSIONS.CREATE_NEW_REPORT,
    )
  ) {
    return `/${analyticsRoutes.reports}/create-report`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.DASHBOARD,
      DASHBOARD_PERMISSIONS.ACCESS_DASHBOARD,
    )
  ) {
    return `/${analyticsRoutes.dashboard}`;
  }
  return null;
};

export const getConfigurationLandingRoute = () => {
  const candidates = [
    {
      route: configuration.paramterList,
      permissions: [
        CONFIGURATION_PERMISSIONS.VIEW_PARAMETER_LIST,
        CONFIGURATION_PERMISSIONS.ADD_PARAMETER,
        CONFIGURATION_PERMISSIONS.UPDATE_PARAMETER,
        CONFIGURATION_PERMISSIONS.DELETE_PARAMETER,
        CONFIGURATION_PERMISSIONS.RESTORE_PARAMETER,
        CONFIGURATION_PERMISSIONS.APPROVE_PARAMETER,
        CONFIGURATION_PERMISSIONS.REORDER_PARAMETERS,
        CONFIGURATION_PERMISSIONS.ADD_PARAMETER_PHASE,
        CONFIGURATION_PERMISSIONS.ADD_PARAMETER_TABLE,
        CONFIGURATION_PERMISSIONS.DELETE_PARAMETER_PHASE,
        CONFIGURATION_PERMISSIONS.DELETE_PARAMETER_TABLE,
      ],
    },
    {
      route: configuration.productList,
      permissions: [
        CONFIGURATION_PERMISSIONS.VIEW_PRODUCT_LIST,
        CONFIGURATION_PERMISSIONS.ADD_PRODUCT,
        CONFIGURATION_PERMISSIONS.UPDATE_PRODUCT,
        CONFIGURATION_PERMISSIONS.APPROVE_PRODUCT,
      ],
    },
    {
      route: configuration.siteList,
      permissions: [
        CONFIGURATION_PERMISSIONS.VIEW_MANUFACTURING_SITE_LIST,
        CONFIGURATION_PERMISSIONS.ADD_MANUFACTURING_SITE,
        CONFIGURATION_PERMISSIONS.UPDATE_MANUFACTURING_SITE,
        CONFIGURATION_PERMISSIONS.APPROVE_MANUFACTURING_SITE,
      ],
    },
    {
      route: configuration.uopList,
      permissions: [
        CONFIGURATION_PERMISSIONS.VIEW_UNIT_OPERATION_LIST,
        CONFIGURATION_PERMISSIONS.ADD_UNIT_OPERATION,
        CONFIGURATION_PERMISSIONS.UPDATE_UNIT_OPERATION,
        CONFIGURATION_PERMISSIONS.APPROVE_UNIT_OPERATION,
      ],
    },
    {
      route: configuration.auditList,
      permissions: [CONFIGURATION_PERMISSIONS.AUDIT_TRAIL],
    },
  ];

  const candidate = candidates.find(({ permissions }) =>
    hasAnyPermission(PERMISSION_MODULES.CONFIGURATION, permissions),
  );
  return candidate ? `/${candidate.route}` : null;
};

export const getGenealogyLandingRoute = () => {
  if (
    hasPermission(
      PERMISSION_MODULES.GENEALOGY,
      GENEALOGY_PERMISSIONS.VIEW_REPORT_LIST,
    )
  ) {
    return `/${analyticsRoutes.genealogy}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.GENEALOGY,
      GENEALOGY_PERMISSIONS.CREATE_NEW_REPORT,
    )
  ) {
    return `/${analyticsRoutes.genealogy}/create`;
  }
  return null;
};

export const getUserManagementLandingRoute = () => {
  if (
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_USER_LIST,
    )
  ) {
    return `/${userManagementRoutes.users}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_ACCESS_GROUP_LIST,
    )
  ) {
    return `/${userManagementRoutes.accessGroups}`;
  }
  if (
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_AUDIT_TRAIL,
    )
  ) {
    return `/${userManagementRoutes.auditTrail}`;
  }
  return null;
};

export const getPostLoginRoute = () => {
  const fullAccessRoutes = [
    [PERMISSION_MODULES.TASK_STATUS, investigatorRoutes.taskStatus],
    [PERMISSION_MODULES.AUDIT_TRAIL, investigatorRoutes.logs],
    [
      PERMISSION_MODULES.SEARCH_AND_INVESTIGATE,
      investigatorRoutes.investigationAnalysis,
    ],
    [PERMISSION_MODULES.DATA_PREVIEW, investigatorRoutes.filesPreview],
  ];
  const permittedFullAccessRoute = fullAccessRoutes.find(([moduleName]) =>
    hasFullAccess(moduleName),
  );

  if (permittedFullAccessRoute) {
    return `/${permittedFullAccessRoute[1]}`;
  }

  const manageDataLandingRoutes = [
    [MANAGE_DATA_PERMISSIONS.VIEW_BATCH_LIST, manageDataRoute.reviewBatches],
    [MANAGE_DATA_PERMISSIONS.VIEW_COPIED_BATCHES, manageDataRoute.copyHistory],
  ];
  const permittedManageDataRoute = manageDataLandingRoutes.find(
    ([permission]) => hasPermission(MANAGE_DATA_MODULE, permission),
  );
  if (permittedManageDataRoute) {
    return `/${permittedManageDataRoute[1]}`;
  }

  return (
    getSrmLandingRoute() ||
    getConfigurationLandingRoute() ||
    getGenealogyLandingRoute() ||
    getUserManagementLandingRoute() ||
    "/page-not-found"
  );
};
